/* eslint-disable @typescript-eslint/no-explicit-any */
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService, DefaultHttpHeaders, HeadersEnum, HttpInputParametersType } from '../api.service';
import { Injectable } from '@angular/core';
import { delay, finalize, take } from 'rxjs/operators';
import { Mocks } from '@core/services/mocks/mocks';
import { select } from '@ngrx/store';
import { getInitFlow } from '@store/flow-data/init-flow';
import { InitFlow } from '@core/models/swagger/initFlow';
import { ClientBlacklistRequest, ClientBlacklistResponse, GenericBlacklistResponse } from '@core/models/swagger/bff';
import { Quote } from '@core/models/swagger';

@Injectable({
  providedIn: 'root',
})
export class ValidationService extends ApiService {
  mocked = false;

  private readonly loadingSubjects: Record<string, BehaviorSubject<boolean>> = {
    postPromotionCode: new BehaviorSubject<boolean>(false),
    getEmail: new BehaviorSubject<boolean>(false),
    getDomain: new BehaviorSubject<boolean>(false),
    getIban: new BehaviorSubject<boolean>(false),
    getPrefix: new BehaviorSubject<boolean>(false),
    getNumber: new BehaviorSubject<boolean>(false),
    getLastname: new BehaviorSubject<boolean>(false),
    postPostCode: new BehaviorSubject<boolean>(false),
    postVertiClient: new BehaviorSubject<boolean>(false),
    postClient: new BehaviorSubject<boolean>(false),
  };

  readonly postPromotionCodeLoading$ = this.loadingSubjects.postPromotionCode.asObservable();
  readonly getEmailLoading$ = this.loadingSubjects.getEmail.asObservable();
  readonly getDomainLoading$ = this.loadingSubjects.getDomain.asObservable();
  readonly getIbanLoading$ = this.loadingSubjects.getIban.asObservable();
  readonly getNumberLoading$ = this.loadingSubjects.getNumber.asObservable();
  readonly getPrefixLoading$ = this.loadingSubjects.getPrefix.asObservable();
  readonly getLastnameLoading$ = this.loadingSubjects.getLastname.asObservable();
  readonly postPostCodeLoading$ = this.loadingSubjects.postPostCode.asObservable();
  readonly postVertiClientLoading$ = this.loadingSubjects.postVertiClient.asObservable();
  readonly postClientLoading$ = this.loadingSubjects.postClient.asObservable();

  private executePostRequest<TRequest, TResponse>({
    endpoint,
    body,
    requestKey,
    headers,
    queryParams,
    mocked,
  }: {
    endpoint: string;
    body: TRequest;
    requestKey: string;
    headers?: DefaultHttpHeaders;
    queryParams?: Record<string, any>;
    mocked?: boolean;
  }): Observable<TResponse> {
    this.loadingSubjects[requestKey].next(true);
    if (this.mocked || mocked) {
      return Mocks[requestKey]().pipe(
        delay(2000),
        finalize(() => this.loadingSubjects[requestKey].next(false))
      );
    }

    this.store.pipe(select(getInitFlow), take(1)).subscribe((initFlow: InitFlow) => {
      headers = {
        ...headers,
        [HeadersEnum.X_CORRELATION_ID]: initFlow?.correlationId?.toString(),
      };
    });

    return this.post<TRequest, TResponse>({ suffix: endpoint, body, headers, queryParams }).pipe(
      finalize(() => this.loadingSubjects[requestKey].next(false))
    );
  }

  private executeGetRequest<TResponse>({
    endpoint,
    queryParams,
    requestKey,
    mocked,
    headers,
  }: {
    endpoint: string;
    queryParams?: HttpInputParametersType;
    requestKey: string;
    mocked?: boolean;
    headers?: DefaultHttpHeaders;
  }): Observable<TResponse> {
    this.loadingSubjects[requestKey].next(true);

    if (this.mocked || mocked) {
      return Mocks[requestKey]().pipe(
        delay(2000),
        finalize(() => this.loadingSubjects[requestKey].next(false))
      );
    }

    this.store.pipe(select(getInitFlow), take(1)).subscribe((initFlow: InitFlow) => {
      headers = {
        ...headers,
        [HeadersEnum.X_CORRELATION_ID]: initFlow?.correlationId?.toString(),
      };
    });

    return this.get<TResponse>({
      suffix: endpoint,
      queryParams,
      headers,
    }).pipe(finalize(() => this.loadingSubjects[requestKey].next(false)));
  }

  postPromotionCode(body: PromotionCodeBody): Observable<PromotionCodeResponse> {
    return this.executePostRequest<PromotionCodeBody, PromotionCodeResponse>({
      endpoint: '/validation/promotioncode',
      body,
      requestKey: 'postPromotionCode',
    });
  }

  getEmail(body: EmailBody): Observable<EmailResponse> {
    const queryParams = {
      emailAddress: body.email,
    };
    return this.executeGetRequest<EmailResponse>({
      endpoint: '/blacklist/email/address',
      queryParams: queryParams,
      requestKey: 'getEmail',
      mocked: false,
    });
  }

  getDomain(domain: DomainBody): Observable<EmailResponse> {
    const queryParams = {
      emailDomain: domain.domain,
    };
    return this.executeGetRequest<EmailResponse>({
      endpoint: '/blacklist/email/domain',
      queryParams: queryParams,
      requestKey: 'getDomain',
      mocked: false,
    });
  }

  getIban(body: IbanBody): Observable<IbanResponse> {
    const fullEndpoint = `/blacklist/iban/${body.iban}`;
    return this.executeGetRequest<EmailResponse>({
      endpoint: fullEndpoint,
      requestKey: 'getIban',
      mocked: false,
    });
  }

  getPrefix(body: NumberBody): Observable<NumberResponse> {
    const fullEndpoint = `/blacklist/telephone/prefix/${body.number}`;
    return this.executeGetRequest<EmailResponse>({
      endpoint: fullEndpoint,
      requestKey: 'getPrefix',
      mocked: false,
    });
  }

  getNumber(body: NumberBody): Observable<NumberResponse> {
    const fullEndpoint = `/blacklist/telephone/number/${body.number}`;
    return this.executeGetRequest<EmailResponse>({
      endpoint: fullEndpoint,
      requestKey: 'getNumber',
      mocked: false,
    });
  }

  postClient(quote: Quote): Observable<ClientBlacklistResponse> {
    const fullEndpoint = `/blacklist/client`;
    return this.executePostRequest<ClientBlacklistRequest, ClientBlacklistResponse>({
      endpoint: '/blacklist/client',
      requestKey: 'postClient',
      body: this.mapQuoteToBlacklistClient(quote),
      mocked: false,
    });
  }

  getLastname(lastname: string): Observable<GenericBlacklistResponse> {
    const queryParams = {
      lastName: lastname,
    };
    return this.executeGetRequest<EmailResponse>({
      endpoint: '/blacklist/lastname/coupon',
      queryParams: queryParams,
      requestKey: 'getLastname',
      mocked: false,
    });
  }

  postVertiClient(body: PartnerSearchBody): Observable<PartnerSearchResponse> {
    return this.executePostRequest<PartnerSearchBody, PartnerSearchResponse>({
      endpoint: '/partner/search',
      body,
      requestKey: 'postVertiClient',
    });
  }

  private mapQuoteToBlacklistClient(quote: Quote): ClientBlacklistRequest {
    return {
      firstName: quote.policyholderData.name,
      lastName: quote.policyholderData.surname,
      dateOfBirth: quote.policyholderData.birthdate,
      postCode: quote.policyholderData.address.postCode,
    };
  }
}

interface PromotionCodeBody {
  cooperationId: string;
  promotionCode: string;
  ratingDate: string;
}
export interface PromotionCodeResponse {
  isValid: boolean;
}

interface EmailBody {
  email: string;
}

interface DomainBody {
  domain: string;
}

export interface EmailResponse {
  isBlacklisted: boolean;
}

export interface IbanResponse {
  isBlacklisted: boolean;
}

export interface NumberResponse {
  isBlacklisted: boolean;
}

interface IbanBody {
  iban: string;
}

interface NumberBody {
  number: string;
}

interface PartnerSearchBody {
  fistName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
}

export interface PartnerSearchResponse {
  isFound: boolean;
  badClient: boolean;
}
